import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';

// Apple Maps-inspired dark mode palette
const C = {
  bgTop: '#0e1722',
  bgBottom: '#1a2334',
  land: '#1f2a3a',
  landDark: '#162033',
  road: '#6b7280',
  roadMajor: '#9ca3af',
  label: '#d1d5db',
  labelDim: '#9ca3af',
  water: '#1e3a5f',
  // POI colors
  mosque: '#10b981',
  kaaba: '#fbbf24',
  hill: '#a78a55',
  cemetery: '#a78bfa',
  blue: '#0a84ff'
};

const LABELS = {
  en: {
    youAreHere: 'You are here',
    blackStone: 'Black Stone',
    maqam: 'Maqam Ibrahim',
    zamzam: 'Zamzam',
    safa: 'Safa',
    marwah: 'Marwah',
    masaa: 'Mas\'a',
    mecca: 'Mecca',
    mina: 'Mina',
    arafah: 'Arafah',
    muzdalifah: 'Muzdalifah',
    sughra: 'Sughra',
    wusta: 'Wusta',
    aqabah: 'Aqabah',
    rawdah: 'Rawdah',
    mihrab: 'Mihrab',
    nabawi: 'Masjid an-Nabawi',
    haram: 'Masjid al-Haram',
    kaaba: 'Kaaba',
    quba: 'Masjid Quba',
    uhud: 'Mt. Uhud',
    baqi: 'Al-Baqi',
    n: 'N',
    title_kaaba: 'Kaaba area',
    title_safa: 'Sa\'i corridor (Mas\'a)',
    title_hajj: 'Mecca → Arafah route',
    title_jamarat: 'Jamarat',
    title_haram: 'Masjid al-Haram',
    title_nabawi: 'Masjid an-Nabawi',
    title_medina: 'Medina'
  },
  ar: {
    youAreHere: 'موقعك',
    blackStone: 'الحجر الأسود',
    maqam: 'مقام إبراهيم',
    zamzam: 'زمزم',
    safa: 'الصفا',
    marwah: 'المروة',
    masaa: 'المسعى',
    mecca: 'مكة',
    mina: 'منى',
    arafah: 'عرفة',
    muzdalifah: 'مزدلفة',
    sughra: 'الصغرى',
    wusta: 'الوسطى',
    aqabah: 'العقبة',
    rawdah: 'الروضة',
    mihrab: 'المحراب',
    nabawi: 'المسجد النبوي',
    haram: 'المسجد الحرام',
    kaaba: 'الكعبة',
    quba: 'مسجد قباء',
    uhud: 'جبل أحد',
    baqi: 'البقيع',
    n: 'ش',
    title_kaaba: 'منطقة الكعبة',
    title_safa: 'المسعى',
    title_hajj: 'مسار مكة ← عرفة',
    title_jamarat: 'الجمرات',
    title_haram: 'المسجد الحرام',
    title_nabawi: 'المسجد النبوي',
    title_medina: 'المدينة'
  }
};

// === MAP BACKGROUND (Apple Maps dark) ===
const MapBackground = ({ id }) => (
  <defs>
    <linearGradient id={`mapBg_${id}`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor={C.bgTop} />
      <stop offset="1" stopColor={C.bgBottom} />
    </linearGradient>
  </defs>
);

// === POI MARKERS (Apple Maps style — small filled circles with icon hints) ===

// Kaaba: small black square with thin gold outline (most authentic representation)
const KaabaPOI = ({ x, y, size = 8 }) => {
  const half = size / 2;
  return (
    <g>
      <rect x={x - half - 1.5} y={y - half - 1.5} width={size + 3} height={size + 3} rx={1} fill="rgba(0,0,0,0.5)" />
      <rect x={x - half} y={y - half} width={size} height={size} fill="#0a0a0a" stroke={C.kaaba} strokeWidth={0.8} />
      <rect x={x - half} y={y - 0.5} width={size} height={1} fill={C.kaaba} />
    </g>
  );
};

// Mosque POI: small green disc with dome silhouette (Apple Maps place-of-worship style)
const MosquePOI = ({ x, y, size = 11 }) => {
  const r = size / 2;
  return (
    <g>
      <circle cx={x} cy={y + 0.5} r={r + 0.5} fill="rgba(0,0,0,0.4)" />
      <circle cx={x} cy={y} r={r} fill={C.mosque} stroke="white" strokeWidth={0.8} />
      {/* tiny dome arc inside */}
      <path d={`M ${x - r * 0.5} ${y + 1} A ${r * 0.5} ${r * 0.5} 0 0 1 ${x + r * 0.5} ${y + 1}`} fill="white" opacity={0.95} />
      <line x1={x} y1={y - 0.5} x2={x} y2={y - r * 0.65} stroke="white" strokeWidth={0.7} opacity={0.9} />
    </g>
  );
};

// Hill / mountain: subtle topographic curve (NOT a triangle)
const HillPOI = ({ x, y, size = 14, label }) => {
  const half = size / 2;
  return (
    <g>
      {/* subtle outer contour */}
      <path d={`M ${x - half - 4} ${y + half} Q ${x} ${y - half - 5} ${x + half + 4} ${y + half} Z`}
        fill={C.hill} opacity={0.35} stroke={C.hill} strokeWidth={0.6} />
      {/* inner contour line */}
      <path d={`M ${x - half + 1} ${y + half - 2} Q ${x} ${y - half + 1} ${x + half - 1} ${y + half - 2}`}
        fill="none" stroke={C.hill} strokeWidth={0.6} opacity={0.7} />
    </g>
  );
};

// Cemetery: grey rectangle with hatching
const CemeteryPOI = ({ x, y, size = 14 }) => {
  const half = size / 2;
  return (
    <g transform={`translate(${x - half}, ${y - half})`}>
      <rect width={size} height={size} fill={C.cemetery} opacity={0.25} stroke={C.cemetery} strokeWidth={0.8} rx={1} />
      <line x1={0} y1={size * 0.5} x2={size} y2={size * 0.5} stroke={C.cemetery} strokeWidth={0.4} opacity={0.6} />
      <line x1={size * 0.5} y1={0} x2={size * 0.5} y2={size} stroke={C.cemetery} strokeWidth={0.4} opacity={0.6} />
    </g>
  );
};

// Tents (Mina): small triangle cluster
const TentsPOI = ({ x, y, size = 10 }) => (
  <g>
    <polygon points={`${x - size * 0.7},${y + size * 0.4} ${x - size * 0.4},${y - size * 0.3} ${x - size * 0.1},${y + size * 0.4}`}
      fill="#dc2626" opacity={0.7} stroke="white" strokeWidth={0.4} />
    <polygon points={`${x - size * 0.1},${y + size * 0.4} ${x + size * 0.2},${y - size * 0.4} ${x + size * 0.5},${y + size * 0.4}`}
      fill="#dc2626" opacity={0.85} stroke="white" strokeWidth={0.4} />
  </g>
);

// Pillar (Jamarat): small dark vertical mark
const PillarPOI = ({ x, y, height = 12 }) => (
  <g>
    <rect x={x - 2.5} y={y - height / 2} width={5} height={height} fill="#374151" stroke="white" strokeWidth={0.6} rx={0.8} />
    <circle cx={x} cy={y - height / 2 + 1.5} r={1.2} fill={C.kaaba} />
  </g>
);

// === CORE: Pulsing blue dot (Apple Maps blue) ===
const PulsingDot = ({ cx, cy }) => (
  <g style={{ transformOrigin: `${cx}px ${cy}px` }}>
    <motion.circle
      cx={cx} cy={cy} r={5} fill={C.blue}
      animate={{ scale: [1, 3, 1], opacity: [0.5, 0, 0.5] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
    />
    <circle cx={cx} cy={cy} r={6.5} fill="white" opacity={0.95} />
    <circle cx={cx} cy={cy} r={5} fill={C.blue} stroke="white" strokeWidth={1.5} />
  </g>
);

// Compass — Apple-style minimal
const Compass = ({ x = 195, y = 20, L }) => (
  <g transform={`translate(${x}, ${y})`}>
    <circle r={11} fill="rgba(0,0,0,0.5)" stroke={C.labelDim} strokeWidth={0.5} />
    <polygon points="0,-7 -2.5,2 0,0 2.5,2" fill="#ef4444" />
    <polygon points="0,7 -2.5,-2 0,0 2.5,-2" fill={C.label} opacity={0.6} />
    <text x={0} y={-13} textAnchor="middle" fontSize={6.5} fill={C.label} fontWeight="bold">{L.n}</text>
  </g>
);

// Smooth curved road
const Road = ({ d, major = false }) => (
  <>
    <path d={d} fill="none" stroke={major ? C.roadMajor : C.road} strokeWidth={major ? 2.8 : 2} opacity={0.85} strokeLinecap="round" />
    <path d={d} fill="none" stroke={C.bgBottom} strokeWidth={major ? 1.4 : 1} opacity={0.6} strokeLinecap="round" />
  </>
);

// Land/terrain blob — soft rounded shape, slightly lighter than bg
const Land = ({ d, opacity = 0.6 }) => (
  <path d={d} fill={C.land} opacity={opacity} />
);

// Compact label — Apple Maps style
const Label = ({ x, y, text, color = C.label, size = 8, weight = 'bold', anchor = 'middle' }) => (
  <text x={x} y={y} textAnchor={anchor} fontSize={size} fill={color} fontWeight={weight}
    style={{ fontFamily: 'system-ui, -apple-system, sans-serif', paintOrder: 'stroke' }}
    stroke={C.bgBottom} strokeWidth={2.5} strokeLinejoin="round" strokeOpacity={0.7}>{text}</text>
);

// === MAP RENDERERS ===

function KaabaMap({ dot, L }) {
  const W = 220, H = 170;
  // Black Stone is at the SE corner of the Kaaba (matching the Salafi
  // Publications reference). Pilgrim starts Tawaf there and walks
  // COUNTER-CLOCKWISE (Kaaba on LEFT shoulder).
  const positions = {
    'black-stone': { x: 124, y: 104 },     // outside SE corner — start of Tawaf
    'tawaf-path': { x: 158, y: 90 },        // east-mid Mataf — mid-Tawaf
    'maqam': { x: 132, y: 100 },             // east of Black Stone
    'zamzam': { x: 105, y: 132 },           // south
    'default': { x: 124, y: 104 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="kaaba" />
      <rect width={W} height={H} fill={`url(#mapBg_kaaba)`} rx={6} />
      {/* Mosque interior land */}
      <Land d={`M 25 38 Q 30 30 110 30 T 195 38 L 200 130 Q 195 145 110 145 T 25 130 Z`} opacity={0.5} />
      {/* Mataf (marble area around Kaaba) — lighter ring */}
      <circle cx={110} cy={90} r={48} fill="#d4d4d8" opacity={0.05} />
      <circle cx={110} cy={90} r={48} fill="none" stroke={C.labelDim} strokeWidth={0.4} strokeDasharray="2 3" opacity={0.7} />
      {/* Tawaf direction — COUNTER-CLOCKWISE: arc traces the Mataf circle exactly
          from 5-o'clock (SE) through 3-o'clock (E) up to 1-o'clock (NE). Both endpoints
          are exactly on the radius-48 circle so the gold arrow overlays the dashed circle. */}
      <path d="M 134 131.57 A 48 48 0 0 0 134 48.43" fill="none" stroke={C.kaaba} strokeWidth={1.6} opacity={0.95} markerEnd="url(#kArr)" />
      <defs>
        <marker id="kArr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill={C.kaaba} />
        </marker>
      </defs>
      {/* Kaaba */}
      <KaabaPOI x={110} y={90} size={11} />
      <Label x={110} y={75} text={L.kaaba} size={7.5} />
      {/* Black Stone — at SE corner of Kaaba */}
      <circle cx={117} cy={97} r={2.2} fill="#000" stroke={C.kaaba} strokeWidth={0.8} />
      <Label x={148} y={104} text={L.blackStone} size={6.5} color={C.kaaba} anchor="start" />
      {/* Maqam Ibrahim — east of Kaaba */}
      <rect x={129} y={100} width={5} height={5} fill={C.mosque} opacity={0.9} stroke="white" strokeWidth={0.5} />
      <Label x={138} y={94} text={L.maqam} size={6.5} color={C.mosque} anchor="start" />
      {/* Zamzam well — south */}
      <circle cx={105} cy={132} r={3} fill="none" stroke={C.blue} strokeWidth={1.2} />
      <circle cx={105} cy={132} r={1.2} fill={C.blue} />
      <Label x={105} y={146} text={L.zamzam} size={6.5} color={C.blue} />
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function SafaMarwahMap({ dot, L }) {
  const W = 220, H = 145;
  const positions = {
    'safa': { x: 35, y: 80 },
    'marwah': { x: 185, y: 80 },
    'middle': { x: 110, y: 80 },
    'default': { x: 35, y: 80 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="safa" />
      <rect width={W} height={H} fill="url(#mapBg_safa)" rx={6} />
      {/* Mosque outline (the corridor is inside the mosque) */}
      <Land d={`M 15 50 Q 20 42 110 42 T 205 50 L 205 110 Q 200 118 110 118 T 15 110 Z`} opacity={0.5} />
      {/* Mas'a corridor — long lighter rectangle */}
      <rect x={50} y={70} width={120} height={20} rx={3} fill="#d4d4d8" opacity={0.08} stroke={C.labelDim} strokeWidth={0.5} strokeDasharray="3 2" />
      <Label x={110} y={62} text={L.masaa} size={7.5} />
      {/* Green-light jog markers */}
      <line x1={88} y1={68} x2={88} y2={92} stroke={C.mosque} strokeWidth={1.8} />
      <line x1={132} y1={68} x2={132} y2={92} stroke={C.mosque} strokeWidth={1.8} />
      {/* Safa hill */}
      <HillPOI x={35} y={80} size={14} />
      <Label x={35} y={113} text={L.safa} size={9} />
      {/* Marwah hill */}
      <HillPOI x={185} y={80} size={12} />
      <Label x={185} y={113} text={L.marwah} size={9} />
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function HajjRouteMap({ dot, L }) {
  // Geographic: Mecca west, Arafah southeast. Real distances ~6km, ~3km, ~5km.
  const W = 220, H = 155;
  const positions = {
    'mecca': { x: 30, y: 50 },
    'mina': { x: 95, y: 60 },
    'muzdalifah': { x: 135, y: 90 },
    'arafah': { x: 188, y: 125 },
    'default': { x: 30, y: 50 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="hajj" />
      <rect width={W} height={H} fill="url(#mapBg_hajj)" rx={6} />
      {/* Mountainous terrain background blobs */}
      <Land d={`M 0 30 Q 60 10 130 25 T 220 35 L 220 0 L 0 0 Z`} opacity={0.4} />
      <Land d={`M 0 100 Q 70 90 130 105 T 220 100 L 220 155 L 0 155 Z`} opacity={0.5} />
      {/* Subtle hill ranges */}
      <path d="M 5 75 Q 30 65 55 78 Q 75 85 95 76" fill="none" stroke={C.hill} strokeWidth={0.6} opacity={0.5} />
      <path d="M 130 110 Q 155 100 175 115 Q 195 125 215 118" fill="none" stroke={C.hill} strokeWidth={0.6} opacity={0.5} />
      {/* Main route */}
      <Road d="M 30 50 Q 60 47 95 60 Q 115 67 135 90 Q 160 110 188 125" major />
      {/* Mecca: mosque POI (Masjid al-Haram) */}
      <MosquePOI x={30} y={50} size={13} />
      <Label x={30} y={36} text={L.mecca} size={8.5} />
      {/* Mina: tents */}
      <TentsPOI x={95} y={60} size={9} />
      <Label x={95} y={45} text={L.mina} size={8.5} />
      {/* Muzdalifah: open ground (oval marker) */}
      <ellipse cx={135} cy={90} rx={9} ry={4} fill={C.cemetery} opacity={0.3} stroke={C.cemetery} strokeWidth={0.8} strokeDasharray="2 1" />
      <circle cx={135} cy={90} r={1.5} fill={C.cemetery} />
      <Label x={135} y={75} text={L.muzdalifah} size={8.5} />
      {/* Arafah: Mt of Mercy */}
      <HillPOI x={188} y={125} size={14} />
      <Label x={188} y={143} text={L.arafah} size={8.5} />
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function JamaratMap({ dot, L }) {
  // Top-down view of Jamarat platform inside Mina
  const W = 220, H = 145;
  const positions = {
    'sughra': { x: 50, y: 75 },
    'wusta': { x: 110, y: 75 },
    'aqabah': { x: 170, y: 75 },
    'default': { x: 170, y: 75 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="jamarat" />
      <rect width={W} height={H} fill="url(#mapBg_jamarat)" rx={6} />
      {/* Multi-level platform background */}
      <rect x={20} y={55} width={180} height={45} rx={4} fill={C.land} opacity={0.7} stroke={C.labelDim} strokeWidth={0.4} />
      <rect x={20} y={55} width={180} height={45} rx={4} fill="none" stroke={C.labelDim} strokeWidth={0.4} strokeDasharray="2 2" opacity={0.4} />
      {/* Walking flow direction arrow */}
      <path d="M 35 47 L 185 47" stroke={C.labelDim} strokeWidth={0.6} strokeDasharray="2 2" opacity={0.5} markerEnd="url(#jArr)" />
      <defs>
        <marker id="jArr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill={C.labelDim} opacity={0.6} />
        </marker>
      </defs>
      {/* Sughra */}
      <PillarPOI x={50} y={75} height={11} />
      <Label x={50} y={115} text={L.sughra} size={8.5} />
      {/* Wusta */}
      <PillarPOI x={110} y={75} height={13} />
      <Label x={110} y={115} text={L.wusta} size={8.5} />
      {/* Aqabah */}
      <PillarPOI x={170} y={75} height={15} />
      <Label x={170} y={115} text={L.aqabah} size={8.5} />
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function MasjidHaramMap({ dot, L }) {
  const W = 220, H = 150;
  const positions = {
    'entrance': { x: 25, y: 80 },
    'kaaba': { x: 110, y: 80 },
    'sai': { x: 175, y: 80 },
    'default': { x: 25, y: 80 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="haram" />
      <rect width={W} height={H} fill="url(#mapBg_haram)" rx={6} />
      {/* Mosque outline */}
      <rect x={15} y={32} width={190} height={88} rx={10} fill={C.mosque} opacity={0.12} stroke={C.mosque} strokeWidth={1} />
      {/* Inner courtyard (mataf) */}
      <circle cx={105} cy={78} r={28} fill="#d4d4d8" opacity={0.07} stroke={C.labelDim} strokeWidth={0.4} strokeDasharray="2 2" />
      {/* Kaaba */}
      <KaabaPOI x={105} y={78} size={10} />
      <Label x={105} y={102} text={L.kaaba} size={7.5} />
      {/* Maqam Ibrahim */}
      <rect x={114} y={68} width={4} height={4} fill={C.mosque} stroke="white" strokeWidth={0.4} />
      {/* Mas'a corridor */}
      <rect x={150} y={62} width={48} height={32} rx={2} fill={C.mosque} opacity={0.18} stroke={C.mosque} strokeWidth={0.6} strokeDasharray="2 2" />
      <Label x={174} y={108} text={L.masaa} size={7} color={C.mosque} />
      {/* Entrance arrow */}
      <path d="M 5 80 L 14 80" stroke={C.kaaba} strokeWidth={1.4} markerEnd="url(#hArr)" />
      <defs>
        <marker id="hArr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill={C.kaaba} />
        </marker>
      </defs>
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function MasjidNabawiMap({ dot, L }) {
  const W = 220, H = 150;
  const positions = {
    'entrance': { x: 25, y: 80 },
    'rawdah': { x: 95, y: 70 },
    'graves': { x: 145, y: 65 },
    'default': { x: 25, y: 80 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="nabawi" />
      <rect width={W} height={H} fill="url(#mapBg_nabawi)" rx={6} />
      {/* Mosque outline */}
      <rect x={15} y={32} width={190} height={88} rx={10} fill={C.mosque} opacity={0.12} stroke={C.mosque} strokeWidth={1} />
      {/* Rawdah area (green carpet) */}
      <rect x={75} y={56} width={50} height={28} rx={2} fill={C.mosque} opacity={0.45} stroke={C.mosque} strokeWidth={0.8} />
      <Label x={100} y={73} text={L.rawdah} size={7.5} color="white" />
      {/* Mihrab notch */}
      <rect x={67} y={84} width={6} height={5} fill={C.kaaba} opacity={0.7} />
      <Label x={70} y={98} text={L.mihrab} size={6} color={C.kaaba} />
      {/* Green Dome over Prophet's chamber */}
      <ellipse cx={145} cy={62} rx={11} ry={8} fill={C.mosque} stroke="white" strokeWidth={0.6} />
      <line x1={145} y1={54} x2={145} y2={48} stroke="white" strokeWidth={0.6} />
      {/* Entrance arrow */}
      <path d="M 5 80 L 14 80" stroke={C.kaaba} strokeWidth={1.4} markerEnd="url(#nArr)" />
      <defs>
        <marker id="nArr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill={C.kaaba} />
        </marker>
      </defs>
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

function MedinaAreaMap({ dot, L }) {
  // Geographic: Uhud ~5km north of Nabawi, Quba ~6km south, Baqi adjacent east
  const W = 220, H = 165;
  const positions = {
    'nabawi': { x: 110, y: 80 },
    'baqi': { x: 138, y: 92 },
    'quba': { x: 80, y: 138 },
    'uhud': { x: 110, y: 28 },
    'default': { x: 110, y: 80 }
  };
  const pos = positions[dot] || positions.default;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <MapBackground id="medina" />
      <rect width={W} height={H} fill="url(#mapBg_medina)" rx={6} />
      {/* Soft rolling terrain */}
      <Land d={`M 0 50 Q 50 35 110 50 T 220 55 L 220 0 L 0 0 Z`} opacity={0.4} />
      <Land d={`M 0 110 Q 80 100 130 115 T 220 110 L 220 165 L 0 165 Z`} opacity={0.45} />
      {/* Roads (curved, smooth) */}
      <Road d="M 110 80 Q 105 55 110 28" />
      <Road d="M 110 80 Q 95 105 80 138" />
      <Road d="M 110 80 Q 122 86 138 92" />
      {/* Mt. Uhud (north) — larger contour */}
      <HillPOI x={110} y={28} size={20} />
      <Label x={110} y={52} text={L.uhud} size={9} />
      {/* Masjid an-Nabawi (center) */}
      <MosquePOI x={110} y={80} size={14} />
      <Label x={110} y={102} text={L.nabawi} size={8.5} />
      {/* Al-Baqi cemetery (just east) */}
      <CemeteryPOI x={138} y={92} size={12} />
      <Label x={138} y={114} text={L.baqi} size={7.5} color={C.cemetery} />
      {/* Quba (south) */}
      <MosquePOI x={80} y={138} size={11} />
      <Label x={80} y={158} text={L.quba} size={8.5} />
      <Compass L={L} />
      <PulsingDot cx={pos.x} cy={pos.y} />
    </svg>
  );
}

const RENDERERS = {
  kaaba: KaabaMap,
  'safa-marwah': SafaMarwahMap,
  'hajj-route': HajjRouteMap,
  jamarat: JamaratMap,
  'masjid-haram': MasjidHaramMap,
  'masjid-nabawi': MasjidNabawiMap,
  'medina-area': MedinaAreaMap
};

const TITLE_KEY = {
  kaaba: 'title_kaaba',
  'safa-marwah': 'title_safa',
  'hajj-route': 'title_hajj',
  jamarat: 'title_jamarat',
  'masjid-haram': 'title_haram',
  'masjid-nabawi': 'title_nabawi',
  'medina-area': 'title_medina'
};

export default function MiniMap({ map, language = 'en' }) {
  if (!map || !map.type) return null;
  const Renderer = RENDERERS[map.type];
  if (!Renderer) return null;
  const isAr = language === 'ar';
  const L = LABELS[language] || LABELS.en;
  const title = L[TITLE_KEY[map.type]] || '';

  return (
    <div
      className="rounded-xl border border-white/15 bg-black/40 backdrop-blur-md p-3 shadow-inner"
      data-testid="mini-map"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <h4 className="text-xs font-bold text-secondary flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" />
          {title}
        </h4>
        <span className="flex items-center gap-1 text-[10px] text-blue-300 font-semibold">
          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          {L.youAreHere}
        </span>
      </div>
      <div className="w-full overflow-hidden rounded-lg">
        <Renderer dot={map.dot} L={L} isAr={isAr} />
      </div>
    </div>
  );
}
